import PropTypes from 'prop-types';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect } from 'react';

import { IconProvider } from '@placekit/uikit/features/IconContext';

import 'styles/global.css';

import MainShell from 'components/MainShell';
import { fontVariables } from 'fonts';
import { icons } from 'icons';
import { UIProvider } from 'features/UIContext';
import { useFirstInteraction } from 'features/useFirstInteraction';
import { useSmoothScroll } from 'features/useSmoothScroll';
import { LocaleProvider, localeFromPath } from 'i18n';

// HubSpot chat (~1 MB with its reCAPTCHA) waits for a first interaction, or this delay.
const CHAT_DELAY = 6000;

const App = ({ Component, pageProps }) => {
  useSmoothScroll();
  const loadChat = useFirstInteraction(CHAT_DELAY);

  // Static export has no i18n routing: the locale comes from the page props
  // (set by the `/fr/**` route files) and falls back to the route path.
  const router = useRouter();
  const locale = pageProps.locale || localeFromPath(router.pathname);

  // `<html lang>` is rendered by _document, which only runs at build time.
  // Client-side navigation (the language switcher) must keep it in sync.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <>
      {/* Font variables on :root (not a wrapper class) so portals (dialogs, notifications) get
          them too. next/head renders it in the static HTML <head>, before hydration. */}
      <Head>
        <style key="font-variables" dangerouslySetInnerHTML={{ __html: fontVariables }} />
      </Head>
      {/* App icon map (icons.json -> src/icons.js), above UIProvider so notifications get it too */}
      <IconProvider icons={icons}>
        <LocaleProvider locale={locale}>
          <UIProvider>
            <MainShell meta={pageProps.meta} nav={pageProps.nav}>
              <Component {...pageProps} />
            </MainShell>
          </UIProvider>
        </LocaleProvider>
      </IconProvider>
      {loadChat && <Script id="hs-script-loader" src="https://js-eu1.hs-scripts.com/27188519.js" />}
    </>
  );
};

App.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object,
};

export default App;
